<template>
    <b-modal id="modal-ticket-list" size="lg" hide-footer scrollable :title="modalTitle" @hidden="onModalHidden">
        <template #modal-title>
            <div class="d-flex align-items-center">
                <i class="fas fa-ticket-alt mr-2"></i>
                <span>Claims & Tickets</span>
                <b-badge variant="info" class="ml-2">{{ tickets.length }} Total</b-badge>
            </div>
        </template>

        <div v-if="loading" class="text-center py-5">
            <b-spinner variant="primary" label="Loading tickets..."></b-spinner>
            <p class="mt-2 text-muted">Loading your claims...</p>
        </div>

        <div v-else-if="tickets.length === 0" class="text-center py-5">
            <i class="fas fa-inbox fa-3x text-muted mb-3"></i>
            <p class="lead">No claims found for this item.</p>
            <p class="text-muted">Click "Create New Claim" to report an issue.</p>
            <b-button variant="primary" @click="createNewTicket" class="mt-2">
                <i class="fas fa-plus-circle mr-1"></i> Create New Claim
            </b-button>
        </div>

        <div v-else>
            <!-- Ticket List -->
            <div class="ticket-list">
                <div v-for="(ticket, index) in tickets" :key="ticket.id" class="ticket-card" :class="{ 'border-left-success': ticket.status === 'resolved', 'border-left-warning': ticket.status === 'open', 'border-left-danger': ticket.status === 'pending' }">
                    <div class="d-flex justify-content-between align-items-start mb-2">
                        <div class="d-flex align-items-center">
                            <span class="ticket-number">{{ index + 1 }}.</span>
                            <h5 class="mb-0 ml-2">Ticket #{{ ticket.id }}</h5>
                            <b-badge :variant="getStatusVariant(ticket.status)" class="ml-2">{{ formatStatus(ticket.status) }}</b-badge>
                        </div>
                        <small class="text-muted">{{ formatDate(ticket.created_on) }}</small>
                    </div>
                    
                    <div class="mb-2">
                        <span class="text-muted mr-2">Action:</span>
                        <strong>{{ ticket.action || 'Not specified' }}</strong>
                    </div>
                    
                    <div v-if="ticket.notes && ticket.notes.length" class="mb-3">
                        <div class="text-muted small mb-1">Latest Note:</div>
                        <div class="note-preview p-2 bg-light rounded">
                            <i class="fas fa-comment-dots mr-1 text-muted"></i>
                            {{ getLatestNote(ticket.notes) }}
                        </div>
                    </div>
                    
                    <div class="d-flex justify-content-between align-items-center mt-2">
                        <div>
                            <span class="badge badge-light">
                                <i class="far fa-clock mr-1"></i>
                                {{ formatRelativeTime(ticket.created_on) }}
                            </span>
                        </div>
                        <b-button size="sm" variant="outline-primary" @click="viewTicket(ticket)">
                            <i class="fas fa-eye mr-1"></i> View Details
                        </b-button>
                    </div>
                </div>
            </div>
            
            <!-- Action Buttons -->
            <div class="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                <b-button variant="outline-secondary" @click="closeModal">
                    <i class="fas fa-times mr-1"></i> Close
                </b-button>
                <b-button variant="primary" @click="createNewTicket">
                    <i class="fas fa-plus-circle mr-1"></i> Create New Claim
                </b-button>
            </div>
        </div>
    </b-modal>
</template>

<script>
import moment from 'moment';

export default {
    name: "TicketListModal",
    props: {
        item: {
            type: Object,
            required: true
        },
        tickets: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            loading: false,
            modalTitle: 'Claims & Tickets'
        };
    },
    methods: {
        formatDate(date) {
            if (!date) return 'N/A';
            return moment(date).format('MMM D, YYYY h:mm A');
        },
        
        formatRelativeTime(date) {
            if (!date) return 'N/A';
            return moment(date).fromNow();
        },
        
        formatStatus(status) {
            const statusMap = {
                'open': 'Open',
                'pending': 'Pending',
                'resolved': 'Resolved',
                'closed': 'Closed'
            };
            return statusMap[status] || status || 'Unknown';
        },
        
        getStatusVariant(status) {
            const variantMap = {
                'open': 'warning',
                'pending': 'danger',
                'resolved': 'success',
                'closed': 'secondary'
            };
            return variantMap[status] || 'secondary';
        },
        
        getLatestNote(notes) {
            if (!notes || !notes.length) return 'No notes available';
            const latest = notes[notes.length - 1];
            return latest.note || latest.text || 'No content';
        },
        
        viewTicket(ticket) {
            this.$emit('view-ticket', ticket);
            this.$bvModal.hide('modal-ticket-list');
        },
        
        createNewTicket() {
            this.$emit('create-ticket', this.item);
            this.$bvModal.hide('modal-ticket-list');
        },
        
        closeModal() {
            this.$bvModal.hide('modal-ticket-list');
        },
        
        onModalHidden() {
            this.$emit('closed');
        }
    }
};
</script>

<style scoped>
.ticket-list {
    max-height: 500px;
    overflow-y: auto;
}

.ticket-card {
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1rem;
    transition: all 0.2s ease;
}

.ticket-card:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
}

.ticket-card.border-left-success {
    border-left: 4px solid #28a745;
}

.ticket-card.border-left-warning {
    border-left: 4px solid #ffc107;
}

.ticket-card.border-left-danger {
    border-left: 4px solid #dc3545;
}

.ticket-number {
    font-weight: 600;
    color: #6c757d;
    font-size: 0.9rem;
}

.note-preview {
    font-size: 0.85rem;
    color: #495057;
    border-left: 3px solid #dee2e6;
    max-height: 60px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

@media print {
    .ticket-list {
        max-height: none;
    }
    
    .ticket-card {
        break-inside: avoid;
        box-shadow: none;
        border: 1px solid #ddd;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/CustomerService/TicketListModal.vue